import React, { forwardRef, useCallback, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { v4 } from 'uuid';
import classNames from 'classnames';
import { Chart } from '../Chart';
import { useDeviceTimeSeriesChart } from './hooks';
import { DeviceChartLegend, DeviceChartXAxis, DeviceChartYAxis } from './components';
import styles from './DeviceChart.module.css';
import { DefaultPieChartColors } from 'common/utils/constants';

const defaultColors = DefaultPieChartColors;
const DeviceChart = forwardRef(function DeviceChart(
  {
    title,
    height,
    counters,
    datasets,
    scales,
    actions,
    editable = false,
    actionsLoading = false,
    actionsEnabled = false,
    actionsVisible = false,
    onClickActionVisible,
    onTimeChange
  },
  forwardedRef
) {
  const [id] = useState(() => v4());
  const handleZoomComplete = useCallback(
    (event) => {
      if (onTimeChange) {
        const { min, max } = event.chart.scales.x;
        onTimeChange({
          from: new Date(min).toISOString(),
          to: new Date(max).toISOString()
        });
      }
    },
    [onTimeChange]
  );
  const chart = useDeviceTimeSeriesChart({
    data: counters,
    datasets,
    scales,
    actionAnnotations: actions,
    enabledZoom: onTimeChange != null,
    enabledMarker: false,
    displayAxisTitle: false,
    displayActionAnnotation: editable ? actionsEnabled : actionsVisible,
    displayUserAnnotation: false,
    displayDataLabel: false,
    displayMarker: false,
    onZoomComplete: handleZoomComplete
  });

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={classNames(styles.left, { [styles.leftEditable]: editable })}>
          <DeviceChartYAxis position="left" scales={chart.options.scales} datasets={datasets} editable={editable} />
        </div>
        <div className={classNames(styles.middle)} style={{ height: height && `${height}px` }}>
          <div className={styles.chart}>
              {/* 
              Previously this was implemented as <Chart ref={forwardedRef} id={id} role="img" aria-label={title} {...chart} /> which spread all properties from useDeviceTimeSeriesChart
              This caused issues because chart.data.datasets was sometimes undefined, causing Chart.js library errors
              Now we explicitly construct the data object and merge it with existing dataset properties to ensure
              proper data structure and prevent runtime errors
            */}
            <Chart
              ref={forwardedRef}
              id={id}
              role="img"
              aria-label={title}
              data={{
                labels: chart.data.labels,
                datasets: datasets.map((dataset, index) => ({
                  ...dataset,
                  data: chart.data.datasets?.[datasets.indexOf(dataset)]?.data || [],
                  borderColor: defaultColors[index],
                  pointBackgroundColor: defaultColors[index],
                  borderWidth: 2,
                  pointRadius: 1,
                  lineTension: 0,
                  spanGaps: true,
                  pointHoverRadius: 2,
                  pointStyle: dataset.type === 'area' ? 'line' : 'circle',
                  type: dataset.type === 'area' ? 'line' : dataset.type,
                  fill: dataset.type === 'area' ? 'origin' : false,
                  segment: {
                    borderDash: ({ p0, p1 }) => {
                      return p0.skip || p1.skip ? [4, 4] : undefined;
                    }
                  }
                }))
              }}
              options={{
                ...chart.options
              }}
              type="line"
            />
          </div>
        </div>
        <div className={classNames(styles.right, { [styles.rightEditable]: editable })}>
          <DeviceChartYAxis position="right" scales={chart.options.scales} datasets={datasets} editable={editable} />
        </div>
      </div>
      <DeviceChartXAxis scales={chart.options.scales} editable={editable} />
      <DeviceChartLegend
        htmlFor={id}
        editable={editable}
        datasets={datasets}
        actionAnnotations={actions}
        actionsLoading={actionsLoading}
        rebootAvailable={editable || actionsEnabled}
        rebootVisible={editable ? actionsEnabled : actionsVisible}
        onClickRebootVisible={onClickActionVisible}
      />
    </div>
  );
});

function DeviceChartPreview(props) {
  const datasets = useWatch({
    name: 'state.datasets'
  });
  const scales = useWatch({
    name: 'state.scales'
  });

  return <DeviceChart datasets={datasets} scales={scales} editable={true} {...props} />;
}

DeviceChart.Preview = DeviceChartPreview;

export default DeviceChart;
